import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, TimePicker } from "material-ui-pickers";

import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Button,
  IconButton,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Collapse,
  Divider,
  Avatar,
  Link as MuiLink,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  createFilterOptions,
} from "@mui/material";

import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Public as PublicIcon,
  AccessTime as AccessTimeIcon,
  Extension as ExtensionIcon,
  FileUpload as FileUploadIcon,
  OpenInNew as OpenInNewIcon,
  Attachment as AttachmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Notes as NotesIcon,
  Storage as DbIcon,
} from "@mui/icons-material";

import { messageShow, upload } from "../../../common/api/actions";
import { createOrUpdate } from "../../api/actions/mutation";
import { getContact, listSaasPlans, listPlanModules, listCrmTags } from "../../api/actions/query";
import { getImageSource } from "../../api/routes";
import { nullToEmptyString, formatAssigneeName } from "../../../../setup/helpers";
import { API_URL } from "../../../../setup/config/env";
import PlanSubscriptionCard from "./PlanSubscriptionCard";
import { checkPermission } from "../../../common/Sidebar/checkPermission";

const filterTagOptions = createFilterOptions();

const BRAND_BLUE = "#0d3559";
const BRAND_ORANGE = "#ea580c";
const BRAND_ORANGE_LIGHT = "#fff7ed";
const BRAND_ORANGE_BORDER = "#fed7aa";

const formatLabel = (key = "") => {
  if (!key) return "";
  let label = key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

  return label
    .replace(/\bJd\b/g, "JD")
    .replace(/\bCv\b/g, "CV")
    .replace(/\bId\b/g, "ID")
    .replace(/\bUrl\b/g, "URL");
};

const isUrl = (value) => {
  if (typeof value !== "string" || !value) return false;
  return value.startsWith("http://") || value.startsWith("https://");
};

const isCloudfrontCvOrJd = (key, value) => {
  if (typeof value !== "string" || !value) return false;
  const lowerKey = key.toLowerCase();
  return (
    value.includes("cloudfront") &&
    (lowerKey.includes("cv") || lowerKey.includes("jd") || lowerKey.includes("file") || lowerKey.includes("doc"))
  );
};

const CreateContacts = (props) => {
  const initialContacts = {
    _id: "",
    contactName: "",
    storeName: "",
    subscribedPlan: "",
    billingCycle: "",
    currency: "",
    activeAddOns: [],
    message: "",
    storeGrowthStage: "",
    phoneNumber: "",
    email: "",
    image: "",
    address: "",
    pincode: "",
    leadStatus: "Contact",
    region: "",
    businessType: "",
    businessDescription: "",
    industryType: "",
    businessWebsite: "",
    socialMediaLinks: {
      Facebook: "",
      Instagram: "",
      LinkedIn: "",
      Twitter: "",
    },
    yearEstablished: "",
    employeeCount: "",
    annualRevenue: "",
    productsServicesOffered: [],
    businessHours: {
      Monday: { start: null, end: null },
      Tuesday: { start: null, end: null },
      Wednesday: { start: null, end: null },
      Thursday: { start: null, end: null },
      Friday: { start: null, end: null },
      Saturday: { start: null, end: null },
      Sunday: { start: null, end: null },
    },
    comments: [],
    modifiedBy: null,
    assignedTo: "",
    leadSource: "Manual",
    tags: [],
    customerType: "",
    extensions: {},
  };

  const [contacts, setContacts] = useState(initialContacts);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const defaultPermissions = { canView: true, canCreate: true, canEdit: true, canDelete: true };
  const [permissions, setPermissions] = useState({
    contactsPermissions: defaultPermissions,
    leadsPermissions: defaultPermissions,
    prospectPermissions: defaultPermissions,
    customerPermissions: defaultPermissions,
  });
  const [reqFrom, setReqFrom] = useState(null);
  const [canInteract, setCanInteract] = useState(true);
  const [is24HourEnabled, setIs24HourEnabled] = useState(false);
  const [showBusinessHours, setShowBusinessHours] = useState(false);
  const [storeTimezone, setStoreTimezone] = useState("Europe/London");
  const [saasPlans, setSaasPlans] = useState([]);
  const [planModules, setPlanModules] = useState([]);
  const [availableTags, setAvailableTags] = useState(() => {
    const fromLocation = props.location?.state?.availableTags || [];
    const fromContact = props.location?.state?.contactData?.tags || [];
    return Array.from(new Set([...fromLocation, ...fromContact]))
      .filter((t) => typeof t === "string" && t.trim().length > 0)
      .map((t) => t.trim());
  });

  useEffect(() => {
    let isMounted = true;
    const fetchTags = async () => {
      try {
        const res = await listCrmTags();
        if (isMounted && res?.data?.success && Array.isArray(res.data.data)) {
          setAvailableTags((prev) => {
            const combined = Array.from(
              new Set([...prev, ...res.data.data, ...(contacts.tags || [])])
            ).filter(Boolean);
            return combined.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
          });
        }
      } catch (err) {
        console.warn("Failed to fetch CRM tags list:", err);
      }
    };
    fetchTags();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(contacts.tags) && contacts.tags.length > 0) {
      setAvailableTags((prev) => {
        const combined = Array.from(new Set([...prev, ...contacts.tags])).filter(Boolean);
        return combined.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      });
    }
  }, [contacts.tags]);

  useEffect(() => {
    const fetchPlanData = async () => {
      try {
        const [plansRes, modulesRes] = await Promise.all([listSaasPlans(), listPlanModules()]);
        if (plansRes?.data?.success) setSaasPlans(plansRes.data.data || []);
        if (modulesRes?.data?.success) setPlanModules(modulesRes.data.data || []);
      } catch (err) {
        console.warn("Failed to fetch SaaS plans/modules in CRM:", err);
      }
    };
    fetchPlanData();
  }, []);

  useEffect(() => {
    const fetchStoreTz = async () => {
      try {
        const response = await fetch(`${API_URL}/api/calendar/settings`);
        const data = await response.json();
        if (data && data.timeZone) {
          setStoreTimezone(data.timeZone);
        }
      } catch (err) {
        console.error("Failed to fetch store timezone:", err);
      }
    };
    fetchStoreTz();
  }, []);

  const [hasInitializedDefaultUser, setHasInitializedDefaultUser] = useState(false);

  useEffect(() => {
    const {
      match: {
        params: { contactId },
      },
      location,
    } = props;

    if (contactId) {
      if (location.state?.contactData) {
        const contactData = location.state.contactData;
        const assignedName = formatAssigneeName(contactData.assignedTo || "");
        setContacts({
          ...initialContacts,
          ...contactData,
          assignedTo: assignedName
        });
      } else {
        getContacts(contactId);
      }
    } else {
      if (!hasInitializedDefaultUser) {
        const rawUser = props.auth?.user?.name || props.auth?.user?.email || "";
        const defaultName = formatAssigneeName(rawUser);
        setContacts((prev) => ({
          ...prev,
          assignedTo: defaultName
        }));
        setHasInitializedDefaultUser(true);
      }
    }
  }, [props.match?.params?.contactId, hasInitializedDefaultUser, props.auth?.user]);

  useEffect(() => {
    const { location, match } = props;
    const contactId = match?.params?.contactId;
    const pathnameParts = (location?.pathname || "").split("/").filter(Boolean);
    const tabFromUrl = match?.params?.tab || (pathnameParts.length >= 3 ? pathnameParts[2] : "contacts");
    const activeTab = location?.state?.activeTab || tabFromUrl || "contacts";
    setReqFrom(activeTab);

    if (location?.state?.permissions) {
      setPermissions(location.state.permissions);
    } else {
      const loadPerms = async () => {
        try {
          const subModuleMap = {
            contacts: "crm.contacts",
            leads: "crm.lead",
            lead: "crm.lead",
            prospect: "crm.prospect",
            prospects: "crm.prospect",
            customer: "crm.customer",
            customers: "crm.customer",
          };
          const subMod = subModuleMap[activeTab.toLowerCase()] || "crm.contacts";
          const perm = await checkPermission("engagement", subMod);
          if (perm) {
            setCanInteract(!!contactId ? Boolean(perm.canEdit) : Boolean(perm.canCreate));
          }
        } catch (e) {
          console.error("Failed to load permissions in CreateOrUpdate:", e);
        }
      };
      loadPerms();
    }
  }, [props.location?.state, props.location?.pathname, props.match?.params?.contactId]);

  useEffect(() => {
    const contactId = props.match?.params?.contactId;
    if (reqFrom && permissions) {
      const permission = permissions[`${reqFrom.toLowerCase()}Permissions`];
      if (permission) {
        setCanInteract(!!contactId ? Boolean(permission.canEdit) : Boolean(permission.canCreate));
      }
    }
  }, [reqFrom, permissions, props.match?.params?.contactId]);

  useEffect(() => {
    if (!contacts || !contacts.businessHours) return;
    const allDaysAre24Hours = Object.values(contacts.businessHours || {}).every((hours) => {
      if (!hours || !hours.start || !hours.end) return false;
      const start = dayjs(hours.start);
      const end = dayjs(hours.end);
      return start.hour() === 0 && start.minute() === 0 && end.hour() === 23 && end.minute() === 59;
    });

    setIs24HourEnabled(allDaysAre24Hours);
  }, [contacts.businessHours]);

  const getContacts = async (contactId) => {
    const { getContact, messageShow } = props;
    try {
      const { data } = await getContact({ contactId });
      if (data.success) {
        const contactData = data.data || {};
        const assignedName = formatAssigneeName(contactData.assignedTo || "");
        setContacts({
          ...initialContacts,
          ...contactData,
          assignedTo: assignedName
        });
      } else {
        messageShow("Failed to fetch contact.");
      }
    } catch (error) {
      messageShow("There was some error. Please try again.");
    }
  };

  const onCreateOrUpdate = async (event) => {
    event.preventDefault();

    const formattedProducts = Array.isArray(contacts.productsServicesOffered)
      ? contacts.productsServicesOffered
      : String(contacts.productsServicesOffered || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);

    const cleanedAssignedTo = formatAssigneeName(contacts.assignedTo || "");

    const formData = {
      ...contacts,
      assignedTo: cleanedAssignedTo,
      leadSource: String(contacts.leadSource || "Manual").trim(),
      tags: Array.isArray(contacts.tags)
        ? contacts.tags.map((t) => String(t).trim()).filter(Boolean)
        : (typeof contacts.tags === "string" ? contacts.tags.split(",").map((t) => t.trim()).filter(Boolean) : []),
      productsServicesOffered: formattedProducts,
    };

    const { createOrUpdate, messageShow } = props;

    setIsLoadingSubmit(true);
    try {
      const { data } = await createOrUpdate({ contacts: formData });

      if (data.success) {
        messageShow(data.message || "Contact saved successfully.");
        back();
      } else {
        if (data.code === 11000) {
          messageShow("Another contact already exists with the same email and phone number.");
        } else {
          messageShow(data.message || "An error occurred. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error in onCreateOrUpdate:", error);
      if (error.response?.data?.message) {
        messageShow(error.response.data.message);
      } else if (error.response?.status === 400) {
        messageShow("Another contact already exists with the same email and phone number.");
      } else {
        messageShow("An error occurred. Please try again.");
      }
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const onType = (event) => {
    const { name, value } = event.target;

    if (name === "assignedTo") {
      setContacts((prevContacts) => ({
        ...prevContacts,
        assignedTo: value
      }));
      return;
    }

    if (name.includes("socialMediaLinks.")) {
      const socialPlatform = name.split(".")[1];
      setContacts((prevContacts) => ({
        ...prevContacts,
        socialMediaLinks: {
          ...(prevContacts.socialMediaLinks || {}),
          [socialPlatform]: value,
        },
      }));
    } else if (name.startsWith("extensions.")) {
      const parts = name.split(".");

      if (parts.length === 3) {
        const extensionKey = parts[1];
        const fieldKey = parts[2];

        setContacts((prevContacts) => {
          const extensions = prevContacts.extensions || {};
          return {
            ...prevContacts,
            extensions: {
              ...extensions,
              [extensionKey]: {
                ...(extensions[extensionKey] || {}),
                [fieldKey]: value,
              },
            },
          };
        });
      } else if (parts.length === 2) {
        const extensionKey = parts[1];

        setContacts((prevContacts) => {
          const extensions = prevContacts.extensions || {};
          return {
            ...prevContacts,
            extensions: {
              ...extensions,
              [extensionKey]: value,
            },
          };
        });
      }
    } else {
      setContacts((prevContacts) => ({ ...prevContacts, [name]: value }));
    }
  };

  const handleProductsServices = (event) => {
    const { value } = event.target;
    setContacts((prevContacts) => ({
      ...prevContacts,
      productsServicesOffered: value
        ? value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        : [],
    }));
  };

  const onUpload = async (event) => {
    const { upload, messageShow } = props;
    const imgFile = event.target.files[0];
    if (!imgFile) return;

    let file = new FormData();
    file.append("type", "contacts");
    file.append("name", contacts.contactName || contacts.storeName || "profile");
    file.append("file", imgFile);

    const fileReader = new FileReader();
    fileReader.onload = () => {
      setPreviewImage(fileReader.result);
    };
    fileReader.readAsDataURL(imgFile);

    try {
      const { data } = await upload(file);
      if (data.success) {
        messageShow("Image uploaded successfully.");
        setContacts((prevContacts) => ({ ...prevContacts, image: data.file }));
      } else {
        messageShow("There was an error uploading the image.");
      }
    } catch (error) {
      messageShow("Upload failed. Please try again.");
    }
  };

  const back = () => {
    const { history } = props;
    history.goBack();
  };

  const onBusinessHoursChange = (day, key, value) => {
    setContacts((prevContacts) => {
      const businessHours = prevContacts.businessHours || {};
      const dayHours = businessHours[day] || {};
      return {
        ...prevContacts,
        businessHours: {
          ...businessHours,
          [day]: { ...dayHours, [key]: value },
        },
      };
    });
  };

  const setAllDaysTo24Hours = (checked) => {
    const newBusinessHours = {};
    Object.keys(contacts.businessHours || {}).forEach((day) => {
      newBusinessHours[day] = checked
        ? {
            start: dayjs().startOf("day").toDate(),
            end: dayjs().hour(23).minute(59).toDate(),
          }
        : {
            start: null,
            end: null,
          };
    });

    setContacts((prevContacts) => ({
      ...prevContacts,
      businessHours: newBusinessHours,
    }));
    setIs24HourEnabled(checked);
  };

  const getContactTimezone = () => {
    return (
      contacts?.extensions?.timeZone ||
      contacts?.extensions?.timezone ||
      contacts?.timeZone ||
      contacts?.timezone ||
      storeTimezone ||
      "Europe/London"
    );
  };

  const formatIsoDateValue = (fieldKey, value) => {
    if (value === null || value === undefined) return "";
    const strVal = String(value);
    if (fieldKey.toLowerCase().includes("scheduled")) {
      return strVal;
    }

    const dateFields = ["createdat", "updatedat", "paymentinitiatedat", "paymentfailedat", "bookedon", "created_at", "updated_at"];
    const isDateField = dateFields.some((f) => fieldKey.toLowerCase().includes(f));
    const isIsoFormat = typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);

    if (isDateField || isIsoFormat) {
      try {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          const tz = getContactTimezone();
          return (
            d.toLocaleString("en-US", {
              timeZone: tz,
              year: "numeric",
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            }) + ` (${tz})`
          );
        }
      } catch (e) {
        // Fallback
      }
    }
    return strVal;
  };

  const getDisplayValue = (extensionKey, fieldKey, value) => {
    if (fieldKey === "paymentMode") {
      if (value === "paid") return "Online Booking";
      if (value === "unpaid") return "Free Booking";
    }
    return formatIsoDateValue(fieldKey, value);
  };

  const openFile = (value) => {
    const url = value.startsWith("http") ? value : getImageSource(value);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderFileCard = (value) => {
    if (!value) return null;
    const fileName = value.split("/").pop() || "Attached Document";
    return (
      <Box
        onClick={() => openFile(value)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.8,
          bgcolor: "#f8fafc",
          border: "1px solid #cbd5e1",
          borderRadius: "6px",
          color: BRAND_BLUE,
          fontWeight: 600,
          fontSize: "13px",
          cursor: "pointer",
          maxWidth: "100%",
          "&:hover": { bgcolor: "rgba(13,53,89,0.06)", borderColor: BRAND_BLUE },
        }}
      >
        <AttachmentIcon sx={{ fontSize: 18, color: BRAND_ORANGE }} />
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 600,
            fontSize: "13px",
            color: BRAND_BLUE,
          }}
        >
          {fileName}
        </Typography>
        <OpenInNewIcon sx={{ fontSize: 14, color: "#64748b" }} />
      </Box>
    );
  };

  const renderFinancialBreakdown = (fieldKey, fieldValue) => {
    const objectItems = fieldValue.filter((v) => typeof v === "object" && v !== null);
    if (objectItems.length === 0) return null;

    if (fieldKey === "paymentHistory") {
      const tz = getContactTimezone();
      return (
        <Grid item xs={12} key={fieldKey} sx={{ mt: 1, mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.8,
              fontWeight: 700,
              fontSize: "11px",
              color: "#64748b",
              display: "block",
              mb: 0.8,
            }}
          >
            {formatLabel(fieldKey)}
          </Typography>
          <Box
            sx={{
              pl: 2,
              py: 1.2,
              borderLeft: `3px solid ${BRAND_BLUE}`,
              backgroundColor: "rgba(13, 53, 89, 0.03)",
              borderRadius: "6px",
            }}
          >
            {objectItems.map((obj, idx) => {
              let status = (obj.status || obj.type || "UNKNOWN").toUpperCase();
              if (status === "ORDER_CREATED" || status === "UPDATED") status = "ORDER CREATED";

              const isSuccess = status === "COMPLETED" || status === "CAPTURED";
              const isFailed = status === "FAILED";
              const isOrder = status === "ORDER CREATED";
              const statusColor = isSuccess ? "#16a34a" : isFailed ? "#dc2626" : isOrder ? "#0284c7" : BRAND_ORANGE;

              const amountStr = obj.amount ? `₹${obj.amount}` : "";
              const paymentIdStr = obj.paymentId ? ` (ID: ${obj.paymentId})` : "";
              const orderIdStr = obj.razorpayOrderId ? ` [Order: ${obj.razorpayOrderId}]` : "";
              const reasonStr = obj.failureReason ? ` - ${obj.failureReason}` : "";
              const timeStr = obj.timestamp
                ? ` @ ${new Date(obj.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZone: tz })}`
                : "";

              return (
                <Typography key={idx} variant="body2" sx={{ mb: 0.5, fontFamily: "monospace", fontSize: "12.5px" }}>
                  <span style={{ color: statusColor, fontWeight: 700 }}>● [{status}]</span> {amountStr}
                  {paymentIdStr}
                  {orderIdStr}
                  {reasonStr}
                  {timeStr}
                </Typography>
              );
            })}
          </Box>
        </Grid>
      );
    }

    return (
      <Grid item xs={12} sm={6} key={fieldKey}>
        <Typography
          variant="caption"
          sx={{
            textTransform: "uppercase",
            letterSpacing: 0.8,
            fontWeight: 700,
            fontSize: "11px",
            color: "#64748b",
            display: "block",
            mb: 0.8,
          }}
        >
          {formatLabel(fieldKey)}
        </Typography>
        <Box sx={{ pl: 1.5, borderLeft: "2px solid #e2e8f0" }}>
          {objectItems.map((obj, idx) => {
            const amount = obj.amount === undefined ? obj.value || 0 : obj.amount;
            return (
              <Typography key={`${obj.name || "item"}-${idx}`} variant="body2" sx={{ mb: 0.4, fontSize: "13px" }}>
                • {obj.name || "Item"}: <strong>{amount}</strong>
              </Typography>
            );
          })}
        </Box>
      </Grid>
    );
  };

  const updateFieldValue = (extensionKey, fieldKey, newValue, isArray = false, idx = null) => {
    setContacts((prev) => {
      const ext = prev.extensions || {};

      if (isArray && idx !== null) {
        const arr = [...(ext[extensionKey] || [])];
        if (arr[idx]) {
          arr[idx] = { ...arr[idx], [fieldKey]: newValue };
          return {
            ...prev,
            extensions: {
              ...ext,
              [extensionKey]: arr,
            },
          };
        }
        return prev;
      }

      if (extensionKey === fieldKey) {
        return {
          ...prev,
          extensions: {
            ...ext,
            [extensionKey]: newValue,
          },
        };
      }

      if (extensionKey.includes(".")) {
        const parts = extensionKey.split(".");
        const newExt = JSON.parse(JSON.stringify(ext));
        let cur = newExt;
        for (const p of parts) {
          if (!cur[p]) cur[p] = {};
          cur = cur[p];
        }
        cur[fieldKey] = newValue;
        return {
          ...prev,
          extensions: newExt,
        };
      }

      return {
        ...prev,
        extensions: {
          ...ext,
          [extensionKey]: {
            ...(ext[extensionKey] || {}),
            [fieldKey]: newValue,
          },
        },
      };
    });
  };

  const renderExtensionField = (extensionKey, fieldKey, fieldValue, onTypeHandler, canEdit, isArray = false, idx = null) => {
    if (fieldValue === undefined) return null;

    // 1. Array data type (e.g. interestedPositions, tags, skills, or array of objects)
    if (Array.isArray(fieldValue)) {
      // Check if it's an array of objects
      const isArrayOfObjects = fieldValue.length > 0 && fieldValue.some((it) => typeof it === "object" && it !== null);

      if (isArrayOfObjects) {
        return (
          <Grid item xs={12} key={fieldKey}>
            <Box sx={{ p: 1.8, border: "1px solid #e2e8f0", borderRadius: "8px", bgcolor: "#f8fafc", mt: 0.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.2 }}>
                <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, fontSize: "11.5px", color: BRAND_BLUE }}>
                  {formatLabel(fieldKey)} ({fieldValue.length})
                </Typography>
                {canEdit && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "11px", py: 0.2, px: 1, textTransform: "none", color: BRAND_BLUE, borderColor: "#cbd5e1" }}
                    onClick={() => {
                      const newArr = [...fieldValue, {}];
                      updateFieldValue(extensionKey, fieldKey, newArr, isArray, idx);
                    }}
                  >
                    + Add Item
                  </Button>
                )}
              </Box>
              <Grid container spacing={2}>
                {fieldValue.map((subItem, sIdx) => (
                  <Grid item xs={12} key={sIdx}>
                    <Box sx={{ p: 1.5, bgcolor: "#ffffff", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>
                          #{sIdx + 1}
                        </Typography>
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newArr = fieldValue.filter((_, i) => i !== sIdx);
                              updateFieldValue(extensionKey, fieldKey, newArr, isArray, idx);
                            }}
                            sx={{ color: "#ef4444", p: 0.5 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Grid container spacing={1.5}>
                        {Object.keys(subItem || {}).map((sK) =>
                          renderExtensionField(
                            isArray ? `${extensionKey}[${idx}].${fieldKey}` : `${extensionKey}.${fieldKey}`,
                            sK,
                            subItem[sK],
                            onTypeHandler,
                            canEdit,
                            true,
                            sIdx
                          )
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        );
      }

      // Array of primitives (Strings, Numbers, etc.) e.g. interestedPositions: ["Software Engineering", "Data Analyst"]
      const arrayValues = fieldValue.map((v) => (v === null || v === undefined ? "" : String(v)));
      const hasUrls = arrayValues.some((v) => isUrl(v) || isCloudfrontCvOrJd(fieldKey, v));

      return (
        <Grid item xs={12} sm={arrayValues.length > 3 ? 12 : 6} key={fieldKey}>
          <Typography
            variant="caption"
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.8,
              fontWeight: 700,
              fontSize: "11px",
              color: "#64748b",
              display: "block",
              mb: 0.8,
            }}
          >
            {formatLabel(fieldKey)}
          </Typography>

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={arrayValues}
            disabled={!canEdit}
            onChange={(event, newValues) => {
              updateFieldValue(extensionKey, fieldKey, newValues, isArray, idx);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="filled"
                  label={option}
                  size="small"
                  sx={{
                    bgcolor: "#e2e8f0",
                    color: "#1e293b",
                    fontWeight: 600,
                    fontSize: "12px",
                    height: 24,
                    mr: 0.5,
                    my: 0.25,
                  }}
                  {...getTagProps({ index })}
                  disabled={!canEdit}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder={canEdit ? "Type value and press Enter" : ""}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  ...params.inputProps,
                  sx: { fontSize: "14px", color: "#1e293b", py: 0.5 },
                }}
              />
            )}
          />

          {hasUrls && (
            <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {arrayValues.map((itemStr, sIdx) =>
                isCloudfrontCvOrJd(fieldKey, itemStr) ? (
                  <Box key={sIdx}>{renderFileCard(itemStr)}</Box>
                ) : isUrl(itemStr) ? (
                  <MuiLink
                    key={sIdx}
                    href={itemStr}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      wordBreak: "break-all",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      color: BRAND_BLUE,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: "#f1f5f9",
                      px: 1,
                      py: 0.4,
                      borderRadius: "4px",
                    }}
                  >
                    {itemStr}
                    <OpenInNewIcon sx={{ fontSize: 13 }} />
                  </MuiLink>
                ) : null
              )}
            </Box>
          )}
        </Grid>
      );
    }

    // 2. Boolean data type
    if (typeof fieldValue === "boolean") {
      return (
        <Grid item xs={12} sm={6} key={fieldKey}>
          <Box sx={{ p: 1.2, border: "1px solid #cbd5e1", borderRadius: "6px", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%", boxSizing: "border-box" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND_BLUE, fontSize: "13px" }}>
              {formatLabel(fieldKey)}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(fieldValue)}
                  disabled={!canEdit}
                  onChange={(e) => {
                    updateFieldValue(extensionKey, fieldKey, e.target.checked, isArray, idx);
                  }}
                  color="primary"
                  size="small"
                />
              }
              label={fieldValue ? "Yes" : "No"}
              sx={{ mr: 0, '& .MuiTypography-root': { fontSize: '12px', fontWeight: 600 } }}
            />
          </Box>
        </Grid>
      );
    }

    // 3. Nested Object data type
    if (typeof fieldValue === "object" && fieldValue !== null) {
      return (
        <Grid item xs={12} key={fieldKey}>
          <Box sx={{ p: 1.8, border: "1px solid #e2e8f0", borderRadius: "6px", bgcolor: "#f8fafc", mt: 0.5 }}>
            <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, fontSize: "11.5px", color: BRAND_BLUE, display: "block", mb: 1.2 }}>
              {formatLabel(fieldKey)}
            </Typography>
            <Grid container spacing={2}>
              {Object.keys(fieldValue).map((subK) =>
                renderExtensionField(
                  `${extensionKey}.${fieldKey}`,
                  subK,
                  fieldValue[subK],
                  onTypeHandler,
                  canEdit,
                  isArray,
                  idx
                )
              )}
            </Grid>
          </Box>
        </Grid>
      );
    }

    // 4. File / CloudFront URL
    const isFile = isCloudfrontCvOrJd(fieldKey, fieldValue);
    const isLink = !isFile && typeof fieldValue === "string" && isUrl(fieldValue);

    // 5. Date / Time data types
    const strVal = String(fieldValue || "");
    const isDateOnly = typeof fieldValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(strVal.trim());
    const isDateTime = typeof fieldValue === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(strVal.trim());

    // 6. Long text data type
    const isLongText = typeof fieldValue === "string" && (
      fieldValue.length > 70 ||
      fieldValue.includes("\n") ||
      /experience|description|project|reason|goal|summary|notes|skills|proficiency|comment|statement|bio|address/i.test(fieldKey)
    );

    // 7. Number data type
    const isNumber = typeof fieldValue === "number";

    return (
      <Grid item xs={12} sm={isLongText ? 12 : 6} key={fieldKey}>
        {isFile && fieldValue && (
          <Box sx={{ mb: 1 }}>{renderFileCard(fieldValue)}</Box>
        )}
        {isLink && fieldValue && (
          <Box sx={{ mb: 0.8 }}>
            <MuiLink
              href={fieldValue}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                wordBreak: "break-all",
                fontSize: "12.5px",
                fontWeight: 600,
                color: BRAND_BLUE,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {fieldValue}
              <OpenInNewIcon sx={{ fontSize: 13 }} />
            </MuiLink>
          </Box>
        )}

        <TextField
          value={
            isDateOnly
              ? strVal.split("T")[0]
              : isDateTime
              ? formatIsoDateValue(fieldKey, fieldValue)
              : getDisplayValue(extensionKey, fieldKey, fieldValue)
          }
          variant="outlined"
          size="small"
          type={isNumber ? "number" : isDateOnly ? "date" : "text"}
          multiline={isLongText}
          minRows={isLongText ? 2 : undefined}
          maxRows={isLongText ? 6 : undefined}
          onChange={(e) => {
            const rawVal = e.target.value;
            const finalVal = isNumber ? (rawVal === "" ? "" : Number(rawVal)) : rawVal;
            updateFieldValue(extensionKey, fieldKey, finalVal, isArray, idx);
          }}
          label={formatLabel(fieldKey)}
          placeholder={`Enter ${formatLabel(fieldKey).toLowerCase()}`}
          fullWidth
          disabled={!canEdit || isDateTime}
          InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
          inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: isLongText ? 0 : 1.1 } }}
          InputProps={
            isCloudfrontCvOrJd(fieldKey, fieldValue)
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Upload File">
                        <IconButton size="small" component="label" sx={{ color: BRAND_BLUE }}>
                          <FileUploadIcon fontSize="small" />
                          <input
                            type="file"
                            hidden
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;

                              const { upload, messageShow } = props;
                              let formData = new FormData();
                              formData.append("type", "crm-extensions");
                              formData.append("file", file);

                              try {
                                const { data } = await upload(formData);
                                if (data.success) {
                                  messageShow("File uploaded successfully.");
                                  updateFieldValue(extensionKey, fieldKey, data.file, isArray, idx);
                                }
                              } catch (err) {
                                console.error("CRM Extension Upload Error:", err);
                                messageShow("Upload failed.");
                              }
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }
              : null
          }
        />
      </Grid>
    );
  };

  const renderExtensionGroupContent = (extensionKey, extensionData) => {
    return (
      <Grid container spacing={2}>
        {typeof extensionData === "object" && extensionData !== null && !Array.isArray(extensionData) ? (
          Object.keys(extensionData).map((fieldKey) => {
            const fieldValue = extensionData[fieldKey];
            return renderExtensionField(extensionKey, fieldKey, fieldValue, onType, canInteract);
          })
        ) : Array.isArray(extensionData) ? (
          extensionData.every((it) => typeof it !== "object" || it === null) ? (
            renderExtensionField(extensionKey, extensionKey, extensionData, onType, canInteract)
          ) : extensionData.length > 1 ? (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                {extensionData.map((item, index) => (
                  <Accordion
                    key={index}
                    defaultExpanded={false}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px !important",
                      bgcolor: "#f8fafc",
                      "&:before": { display: "none" },
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />}
                      sx={{
                        minHeight: 40,
                        px: 1.5,
                        "& .MuiAccordionSummary-content": { my: 0.6, display: "flex", alignItems: "center", gap: 1 },
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "12.5px" }}>
                        {formatLabel(extensionKey)} #{index + 1}
                      </Typography>
                      {item?.title || item?.name || item?.status || item?.paymentMode ? (
                        <Chip
                          label={String(item.title || item.name || item.status || item.paymentMode)}
                          size="small"
                          sx={{ height: 20, fontSize: "11px", bgcolor: "#e2e8f0", color: "#334155" }}
                        />
                      ) : null}
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pb: 2, pt: 1, bgcolor: "#ffffff", borderTop: "1px solid #e2e8f0" }}>
                      <Grid container spacing={2}>
                        {Object.keys(item || {}).map((fieldKey) => {
                          const fieldValue = item[fieldKey];

                          const isFreeBooking = item.paymentMode === "unpaid" || item.paymentStatus === "not_required";
                          const financialFields = ["subTotal", "taxes", "charges", "discounts", "grandTotal", "totalAmount"];
                          if (isFreeBooking && financialFields.includes(fieldKey)) {
                            return null;
                          }

                          if (financialFields.includes(fieldKey) && Array.isArray(fieldValue) && fieldValue.every(it => typeof it === "object" && it !== null && ("amount" in it || "value" in it))) {
                            return renderFinancialBreakdown(fieldKey, fieldValue);
                          }

                          return renderExtensionField(
                            extensionKey,
                            fieldKey,
                            fieldValue,
                            onType,
                            canInteract,
                            true,
                            index
                          );
                        })}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Grid>
          ) : (
            extensionData.map((item, index) => (
              <Grid item xs={12} key={index}>
                <Box
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    bgcolor: "#f8fafc",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: BRAND_BLUE,
                      fontSize: "12px",
                      display: "block",
                      mb: 1.5,
                    }}
                  >
                    {formatLabel(extensionKey)} #{index + 1}
                  </Typography>

                  <Grid container spacing={2}>
                    {Object.keys(item || {}).map((fieldKey) => {
                      const fieldValue = item[fieldKey];

                      const isFreeBooking = item.paymentMode === "unpaid" || item.paymentStatus === "not_required";
                      const financialFields = ["subTotal", "taxes", "charges", "discounts", "grandTotal", "totalAmount"];
                      if (isFreeBooking && financialFields.includes(fieldKey)) {
                        return null;
                      }

                      if (financialFields.includes(fieldKey) && Array.isArray(fieldValue) && fieldValue.every(it => typeof it === "object" && it !== null && ("amount" in it || "value" in it))) {
                        return renderFinancialBreakdown(fieldKey, fieldValue);
                      }

                      return renderExtensionField(
                        extensionKey,
                        fieldKey,
                        fieldValue,
                        onType,
                        canInteract,
                        true,
                        index
                      );
                    })}
                  </Grid>
                </Box>
              </Grid>
            ))
          )
        ) : (
          <Grid item xs={12} sm={6}>
            {isCloudfrontCvOrJd(extensionKey, extensionData) && extensionData ? (
              <Box sx={{ mt: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    fontWeight: 700,
                    fontSize: "11px",
                    color: "#64748b",
                    display: "block",
                    mb: 0.8,
                  }}
                >
                  {formatLabel(extensionKey)}
                </Typography>
                {renderFileCard(extensionData)}
              </Box>
            ) : isUrl(extensionData) ? (
              <Box sx={{ mt: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    fontWeight: 700,
                    fontSize: "11px",
                    color: "#64748b",
                    display: "block",
                    mb: 0.8,
                  }}
                >
                  {formatLabel(extensionKey)}
                </Typography>
                <MuiLink
                  href={extensionData}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    wordBreak: "break-all",
                    fontSize: "13.5px",
                    fontWeight: 600,
                    color: BRAND_BLUE,
                    mt: 0.5,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {extensionData}
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </MuiLink>
              </Box>
            ) : (
              <TextField
                name={`extensions.${extensionKey}`}
                value={getDisplayValue(extensionKey, extensionKey, extensionData)}
                variant="outlined"
                size="small"
                onChange={(e) => updateFieldValue(extensionKey, extensionKey, e.target.value)}
                label={formatLabel(extensionKey)}
                placeholder={`Enter ${formatLabel(extensionKey).toLowerCase()}`}
                fullWidth
                disabled={!canInteract}
                InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
              />
            )}
          </Grid>
        )}
      </Grid>
    );
  };

  const renderDynamicExtensionsSection = () => {
    const extensions = contacts.extensions || {};

    const filteredExtensions = Object.keys(extensions).filter((key) => {
      const lowerKey = key.toLowerCase();
      return !lowerKey.includes("submission") && lowerKey !== "timezone" && extensions[key] !== undefined;
    });

    if (filteredExtensions.length === 0) {
      return null;
    }

    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: "8px",
          mb: 2.5,
          p: 2.5,
          bgcolor: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <ExtensionIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
            Extensions & Custom Data
          </Typography>
          <Chip
            label={`${filteredExtensions.length} ${filteredExtensions.length === 1 ? "group" : "groups"}`}
            size="small"
            sx={{ bgcolor: BRAND_ORANGE_LIGHT, color: BRAND_ORANGE, border: `1px solid ${BRAND_ORANGE_BORDER}`, fontWeight: 700, fontSize: "11.5px", height: 22 }}
          />
        </Box>
        <Divider sx={{ mb: 2, borderColor: "#f1f5f9" }} />

        {filteredExtensions.map((extensionKey) => {
          const extensionData = extensions[extensionKey];
          const fieldCount = (typeof extensionData === "object" && extensionData !== null)
            ? (Array.isArray(extensionData) ? extensionData.length : Object.keys(extensionData).length)
            : 1;

          if (filteredExtensions.length > 1) {
            return (
              <Accordion
                key={extensionKey}
                defaultExpanded={false}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px !important",
                  bgcolor: "#ffffff",
                  mb: 1.5,
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 20, color: BRAND_BLUE }} />}
                  sx={{
                    minHeight: 46,
                    px: 2,
                    bgcolor: "#f8fafc",
                    "& .MuiAccordionSummary-content": { my: 0.8, display: "flex", alignItems: "center", gap: 1.2 },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "13.5px" }}>
                    {formatLabel(extensionKey)}
                  </Typography>
                  <Chip
                    label={`${fieldCount} ${Array.isArray(extensionData) ? "items" : "fields"}`}
                    size="small"
                    sx={{ height: 22, fontSize: "11.5px", bgcolor: "#e2e8f0", color: "#475569", fontWeight: 600 }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2.5, bgcolor: "#ffffff", borderTop: "1px solid #e2e8f0" }}>
                  {renderExtensionGroupContent(extensionKey, extensionData)}
                </AccordionDetails>
              </Accordion>
            );
          }

          return (
            <Box key={extensionKey} sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: BRAND_BLUE,
                  fontSize: "13.5px",
                  pb: 0.8,
                  mb: 1.5,
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                {formatLabel(extensionKey)}
              </Typography>
              {renderExtensionGroupContent(extensionKey, extensionData)}
            </Box>
          );
        })}
      </Card>
    );
  };

  const isEditing = Boolean(props.match?.params?.contactId);
  const displayTitle = isEditing ? "Edit Contact" : "Create Contact";
  const avatarImage = previewImage || (contacts.image ? getImageSource(contacts.image) : null);

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, maxWidth: 1200, mx: "auto" }}>
        {/* Top Header & Toolbar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2.5,
            pb: 1.5,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              color="inherit"
              onClick={back}
              edge="start"
              sx={{
                mr: 1,
                color: BRAND_BLUE,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: BRAND_BLUE,
                  fontSize: { xs: "17px", sm: "19px" },
                  lineHeight: 1.2,
                }}
              >
                {displayTitle}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "12px", mt: 0.2 }}>
                {contacts.storeName || contacts.contactName || "Contact & Business Information"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
              onClick={back}
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "12.5px",
                borderColor: "#cbd5e1",
                color: "#64748b",
                py: "4px",
                px: 1.8,
                "&:hover": { bgcolor: "#f8fafc", borderColor: "#94a3b8" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={isLoadingSubmit ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 15 }} />}
              onClick={onCreateOrUpdate}
              disabled={isLoadingSubmit || !canInteract}
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "12.5px",
                bgcolor: BRAND_BLUE,
                color: "#ffffff",
                py: "4px",
                px: 2,
                "&:hover": { bgcolor: "#08223a" },
              }}
            >
              {isLoadingSubmit ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {Boolean((contacts?.assignedTo) && (contacts.assignedTo).trim()) && (
            <Chip
              label={`Assigned to: ${formatAssigneeName(contacts.assignedTo)}${
                contacts?.createdDate || contacts?.createdAt
                  ? ` on ${dayjs(contacts.createdDate || contacts.createdAt).format("DD MMM YYYY, hh:mm A")}`
                  : ""
              }`}
              sx={{
                bgcolor: "#e0f2fe",
                color: "#0369a1",
                border: "1px solid #bae6fd",
                fontWeight: 600,
                fontSize: "11.5px",
                height: 24,
              }}
            />
          )}
          {contacts?.modifiedBy?.name && (
            <Chip
              label={`Last modified by: ${contacts.modifiedBy.name} on ${
                contacts?.updatedAt ? dayjs(contacts.updatedAt).format("DD MMM YYYY, hh:mm A") : "-"
              }`}
              sx={{
                bgcolor: BRAND_ORANGE_LIGHT,
                color: BRAND_ORANGE,
                border: `1px solid ${BRAND_ORANGE_BORDER}`,
                fontWeight: 600,
                fontSize: "11.5px",
                height: 24,
              }}
            />
          )}
        </Box>

        <form onSubmit={onCreateOrUpdate}>
          <Grid container spacing={3}>
            {/* Main Form Body */}
            <Grid item xs={12} lg={isEditing && contacts.customerType === "Client Portal Signup" ? 8 : 12}>
              {/* Card 1: Contact & Business Information */}
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  mb: 2.5,
                  p: 2.5,
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <PersonIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                    Contact & Business Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2.5, borderColor: "#f1f5f9" }} />

                {/* Profile Photo / Avatar Upload Strip */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 2.5, p: 1.8, bgcolor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", flexWrap: "wrap" }}>
                  <Avatar
                    src={avatarImage || undefined}
                    alt={contacts.contactName || contacts.storeName || "Profile"}
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      bgcolor: BRAND_BLUE,
                      fontSize: 22,
                      fontWeight: 700,
                    }}
                  >
                    {(contacts.contactName || contacts.storeName || "C").charAt(0).toUpperCase()}
                  </Avatar>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                      <input
                        accept="image/png,image/jpeg,image/webp"
                        style={{ display: "none" }}
                        id="crm-contact-photo-upload"
                        type="file"
                        onChange={onUpload}
                        disabled={!canInteract}
                      />
                      <label htmlFor="crm-contact-photo-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          size="small"
                          startIcon={<FileUploadIcon sx={{ fontSize: 15 }} />}
                          disabled={!canInteract}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "12px",
                            borderColor: BRAND_BLUE,
                            color: BRAND_BLUE,
                            borderRadius: "6px",
                            py: "3px",
                            px: 1.5,
                            "&:hover": { bgcolor: "rgba(13,53,89,0.06)", borderColor: BRAND_BLUE },
                          }}
                        >
                          Upload Image
                        </Button>
                      </label>
                      {contacts.image && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            setContacts((prev) => ({ ...prev, image: "" }));
                            setPreviewImage(null);
                          }}
                          disabled={!canInteract}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "12px",
                            color: "#dc2626",
                            py: "3px",
                            "&:hover": { bgcolor: "#fef2f2" },
                          }}
                        >
                          Remove Image
                        </Button>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11px" }}>
                      Store logo or contact photo (PNG, JPG, or WEBP up to 5MB)
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="storeName"
                      value={nullToEmptyString(contacts.storeName)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Store Name"
                      placeholder="Enter Store Name"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="contactName"
                      value={nullToEmptyString(contacts.contactName)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Contact Person Name"
                      placeholder="Enter contact person name"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="phoneNumber"
                      value={nullToEmptyString(contacts.phoneNumber)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Phone Number"
                      placeholder="Enter phone number"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="email"
                      value={nullToEmptyString(contacts.email)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Email"
                      placeholder="Enter email"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  {/* Assigned To - editable field */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="assignedTo"
                      value={nullToEmptyString(contacts.assignedTo)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      onBlur={(e) => {
                        const formatted = formatAssigneeName(e.target.value);
                        setContacts((prev) => ({
                          ...prev,
                          assignedTo: formatted
                        }));
                      }}
                      label="Assigned To"
                      placeholder="Enter Assignee Name"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Card 2: Location & Address */}
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  mb: 2.5,
                  p: 2.5,
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <LocationIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                    Location & Address
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2, borderColor: "#f1f5f9" }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="address"
                      value={
                        contacts.address
                          ? typeof contacts.address === "object"
                            ? Object.values(contacts.address).join(", ")
                            : contacts.address
                          : ""
                      }
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Address"
                      placeholder="Enter address"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="region"
                      value={nullToEmptyString(contacts.region)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Region"
                      placeholder="Enter region"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="pincode"
                      value={nullToEmptyString(contacts.pincode)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Pin Code"
                      placeholder="Enter pin code"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Card 3: Business Details */}
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  mb: 2.5,
                  p: 2.5,
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <BusinessIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                    Business Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2, borderColor: "#f1f5f9" }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="leadStatus"
                      value={nullToEmptyString(contacts.leadStatus || "Contact")}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Lead Status"
                      select
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                    >
                      {permissions?.contactsPermissions?.canCreate && (
                        <MenuItem value="Contact">Contact</MenuItem>
                      )}
                      {permissions?.leadsPermissions?.canCreate && (
                        <MenuItem value="Lead">Lead</MenuItem>
                      )}
                      {permissions?.prospectPermissions?.canCreate && (
                        <MenuItem value="Prospect">Prospect</MenuItem>
                      )}
                      {permissions?.customerPermissions?.canCreate && (
                        <MenuItem value="Customer">Customer</MenuItem>
                      )}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="customerType"
                      value={nullToEmptyString(contacts.customerType)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Customer Type"
                      placeholder="Enter customer type"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="businessType"
                      value={nullToEmptyString(contacts.businessType)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Business Size"
                      select
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                    >
                      <MenuItem value="Small">Small</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="Large">Large</MenuItem>
                      <MenuItem value="Masterclass Registration">Masterclass Registration</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="leadSource"
                      value={nullToEmptyString(contacts.leadSource || "Manual")}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Lead Source"
                      select
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                    >
                      <MenuItem value="Google Search">Google Search</MenuItem>
                      <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                      <MenuItem value="Website">Website</MenuItem>
                      <MenuItem value="Referral">Referral</MenuItem>
                      <MenuItem value="Manual">Manual</MenuItem>
                      <MenuItem value="Campaign">Campaign</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="industryType"
                      value={nullToEmptyString(contacts.industryType)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Industry Type"
                      placeholder="Enter Industry Type"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="storeGrowthStage"
                      value={nullToEmptyString(contacts.storeGrowthStage)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Growth Stage"
                      select
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                    >
                      <MenuItem value="Startup">Startup</MenuItem>
                      <MenuItem value="Growing">Growing</MenuItem>
                      <MenuItem value="Established">Established</MenuItem>
                      <MenuItem value="Enterprise">Enterprise</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="yearEstablished"
                      value={nullToEmptyString(contacts.yearEstablished)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Year Established"
                      placeholder="Enter year established"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="employeeCount"
                      value={nullToEmptyString(contacts.employeeCount)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Employee Count"
                      placeholder="Enter employee count"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*", sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="annualRevenue"
                      value={nullToEmptyString(contacts.annualRevenue)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Annual Revenue"
                      placeholder="Enter annual revenue"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      forcePopupIcon
                      filterSelectedOptions
                      options={availableTags}
                      value={Array.isArray(contacts.tags) ? contacts.tags : []}
                      onChange={(event, newValue) => {
                        const cleanedTags = Array.from(
                          new Set(
                            newValue
                              .flatMap((item) => {
                                if (typeof item === "string") return item.split(",");
                                if (item && item.inputValue) return item.inputValue.split(",");
                                if (item && item.title && !item.inputValue) return item.title.split(",");
                                return [];
                              })
                              .map((item) => String(item).trim())
                              .filter(Boolean)
                          )
                        );
                        setContacts((prev) => ({
                          ...prev,
                          tags: cleanedTags,
                        }));
                        setAvailableTags((prev) => {
                          const combined = Array.from(new Set([...prev, ...cleanedTags]));
                          return combined.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
                        });
                      }}
                      filterOptions={(options, params) => {
                        const filtered = filterTagOptions(options, params);
                        const { inputValue } = params;
                        const trimmed = (inputValue || "").trim();
                        const isExisting = options.some(
                          (option) => trimmed.toLowerCase() === (typeof option === "string" ? option.toLowerCase() : "")
                        );
                        if (trimmed !== "" && !isExisting) {
                          filtered.push({
                            inputValue: trimmed,
                            title: `Add "${trimmed}"`,
                          });
                        }
                        return filtered;
                      }}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        if (option && option.inputValue) return option.inputValue;
                        if (option && option.title) return option.title;
                        return "";
                      }}
                      renderOption={(props, option) => {
                        const isNew = typeof option === "object" && option?.inputValue;
                        return (
                          <li {...props} key={typeof option === "string" ? option : option.inputValue}>
                            {isNew ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#0284c7", fontWeight: 600 }}>
                                <span>+</span> {option.title}
                              </Box>
                            ) : (
                              option
                            )}
                          </li>
                        );
                      }}
                      disabled={!canInteract}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            size="small"
                            label={option}
                            {...getTagProps({ index })}
                            sx={{
                              bgcolor: "#e0f2fe",
                              color: "#0369a1",
                              borderColor: "#bae6fd",
                              fontWeight: 600,
                              fontSize: "12px",
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label="Tags"
                          placeholder={contacts.tags?.length ? "" : "Select existing tags or type to add new"}
                          InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="productsServicesOffered"
                      value={
                        Array.isArray(contacts.productsServicesOffered)
                          ? contacts.productsServicesOffered.join(", ")
                          : contacts.productsServicesOffered || ""
                      }
                      variant="outlined"
                      size="small"
                      onChange={handleProductsServices}
                      label="Products/Services Offered"
                      placeholder="Product 1, Product 2, Product 3"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="businessDescription"
                      value={nullToEmptyString(contacts.businessDescription)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Description"
                      placeholder="Enter store description"
                      multiline
                      rows={3}
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b" } }}
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Card 4: Web & Social Media */}
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  mb: 2.5,
                  p: 2.5,
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <PublicIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                    Web & Social Media
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2, borderColor: "#f1f5f9" }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="businessWebsite"
                      value={nullToEmptyString(contacts.businessWebsite)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Business Website"
                      placeholder="Enter business website"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="socialMediaLinks.LinkedIn"
                      value={nullToEmptyString(contacts.socialMediaLinks?.LinkedIn)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="LinkedIn Link"
                      placeholder="Enter LinkedIn link"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="socialMediaLinks.Facebook"
                      value={nullToEmptyString(contacts.socialMediaLinks?.Facebook)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Facebook Link"
                      placeholder="Enter Facebook link"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="socialMediaLinks.Instagram"
                      value={nullToEmptyString(contacts.socialMediaLinks?.Instagram)}
                      variant="outlined"
                      size="small"
                      onChange={onType}
                      label="Instagram Link"
                      placeholder="Enter Instagram link"
                      fullWidth
                      disabled={!canInteract}
                      InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                      inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Card 5: Extensions & Custom Data */}
              {renderDynamicExtensionsSection()}

              {/* Card 6: Business Hours */}
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  mb: 2.5,
                  p: 2.5,
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
              >
                <Box
                  onClick={() => setShowBusinessHours((prev) => !prev)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                      Business Hours
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ color: BRAND_BLUE, p: "4px" }}>
                    {showBusinessHours ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                </Box>

                <Collapse in={showBusinessHours}>
                  <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={is24HourEnabled}
                          onChange={(e) => setAllDaysTo24Hours(e.target.checked)}
                          disabled={!canInteract}
                          size="small"
                        />
                      }
                      label={<Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#334155" }}>Full Day Hours</Typography>}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    {Object.keys(contacts.businessHours || {}).map((day) => (
                      <Grid item xs={12} sm={6} md={4} key={day}>
                        <Box sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "13px", mb: 1 }}>
                            {day}
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <TimePicker
                                label="Start Time"
                                value={contacts.businessHours[day]?.start}
                                onChange={(time) => onBusinessHoursChange(day, "start", time)}
                                fullWidth
                                disabled={!canInteract}
                                InputLabelProps={{ shrink: true, style: { fontSize: "11px" } }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TimePicker
                                label="End Time"
                                value={contacts.businessHours[day]?.end}
                                onChange={(time) => onBusinessHoursChange(day, "end", time)}
                                fullWidth
                                disabled={!canInteract}
                                InputLabelProps={{ shrink: true, style: { fontSize: "11px" } }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Collapse>
              </Card>

              {/* Card 7: Note */}
              <Card
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  mb: 2.5,
                  p: 2.5,
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <NotesIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                    Note
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2, borderColor: "#f1f5f9" }} />

                <TextField
                  name="message"
                  value={nullToEmptyString(contacts.message)}
                  variant="outlined"
                  size="small"
                  onChange={onType}
                  label="Note"
                  placeholder="Enter Note"
                  multiline
                  rows={3}
                  fullWidth
                  disabled={!canInteract}
                  InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                  inputProps={{ sx: { fontSize: "14px", color: "#1e293b" } }}
                />
              </Card>

              {/* Dynamic Additional Document Metadata (Ensures 100% of collection fields are visible & editable) */}
              {(() => {
                const knownKeys = [
                  "_id", "__v", "isDeleted", "password", "otp", "emailOtp", "mobileOtp", "modifiedBy",
                  "extensions", "comments", "storeName", "contactName", "email", "phoneNumber", "address",
                  "pincode", "region", "location", "image", "businessType", "storeGrowthStage", "subscribedPlan",
                  "leadStatus", "industryType", "businessWebsite", "yearEstablished", "employeeCount", "taxId",
                  "subdomain", "currency", "productsServicesOffered", "businessDescription", "socialMediaLinks",
                  "businessHours", "isWhatsappSubscribed", "whatsappChatStarted", "emailSubscription",
                  "whatsappSubscription", "isEmailVerified", "isMobileVerified", "isVerified", "message",
                  "onboardingStep", "temporalWorkflowId", "clientConfigId", "stripeCustomerId",
                  "stripeSubscriptionId", "stripeSubscriptionStatus", "stripeSubscriptionCurrentPeriodEnd",
                  "stripeSubscriptionCancelAtPeriodEnd", "subscriptionCanceledAt", "stripeLastPaymentError",
                  "stripeLastPaymentErrorAt", "activeAddOns", "paymentMethod", "planCustomization",
                  "createdDate", "updatedDate", "createdAt", "updatedAt", "timezone", "userId", "customerType"
                ];
                const extraKeys = Object.keys(contacts).filter(
                  (k) => !knownKeys.includes(k) && contacts[k] !== undefined && contacts[k] !== null && contacts[k] !== ""
                );

                if (extraKeys.length === 0) return null;

                return (
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: "8px",
                      mb: 2.5,
                      p: 2.5,
                      bgcolor: "#ffffff",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <DbIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
                        Additional Document Metadata
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2, borderColor: "#f1f5f9" }} />

                    <Grid container spacing={2}>
                      {extraKeys.map((k) => {
                        const val = contacts[k];
                        if (typeof val === "boolean") {
                          return (
                            <Grid item xs={12} sm={6} key={k}>
                              <Box sx={{ p: 1.2, border: "1px solid #cbd5e1", borderRadius: "6px", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%", boxSizing: "border-box" }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND_BLUE, fontSize: "13px" }}>
                                  {formatLabel(k)}
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={Boolean(val)}
                                      disabled={!canInteract}
                                      onChange={(e) => {
                                        const newVal = e.target.checked;
                                        setContacts((prev) => ({ ...prev, [k]: newVal }));
                                      }}
                                      color="primary"
                                      size="small"
                                    />
                                  }
                                  label={val ? "Yes" : "No"}
                                  sx={{ mr: 0, '& .MuiTypography-root': { fontSize: '12px', fontWeight: 600 } }}
                                />
                              </Box>
                            </Grid>
                          );
                        }

                        if (typeof val === "object" && val !== null) {
                          return (
                            <Grid item xs={12} key={k}>
                              <Box sx={{ p: 1.8, border: "1px solid #e2e8f0", borderRadius: "6px", bgcolor: "#f8fafc" }}>
                                <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, fontSize: "11.5px", color: BRAND_BLUE, display: "block", mb: 1.2 }}>
                                  {formatLabel(k)}
                                </Typography>
                                <Grid container spacing={2}>
                                  {Object.keys(val).map((subKey) => (
                                    <Grid item xs={12} sm={6} key={subKey}>
                                      <TextField
                                        name={`${k}.${subKey}`}
                                        value={typeof val[subKey] === "object" ? JSON.stringify(val[subKey]) : val[subKey] ?? ""}
                                        variant="outlined"
                                        size="small"
                                        onChange={(e) => {
                                          const newVal = e.target.value;
                                          setContacts((prev) => ({
                                            ...prev,
                                            [k]: { ...(prev[k] || {}), [subKey]: newVal }
                                          }));
                                        }}
                                        label={formatLabel(subKey)}
                                        placeholder={`Enter ${formatLabel(subKey).toLowerCase()}`}
                                        fullWidth
                                        disabled={!canInteract}
                                        InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                                        inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            </Grid>
                          );
                        }

                        return (
                          <Grid item xs={12} sm={6} key={k}>
                            <TextField
                              name={k}
                              value={nullToEmptyString(val)}
                              variant="outlined"
                              size="small"
                              onChange={onType}
                              label={formatLabel(k)}
                              placeholder={`Enter ${formatLabel(k).toLowerCase()}`}
                              fullWidth
                              disabled={!canInteract}
                              InputLabelProps={{ shrink: true, sx: { fontSize: "13px", fontWeight: 600, color: "#64748b" } }}
                              inputProps={{ sx: { fontSize: "14px", color: "#1e293b", py: 1.1 } }}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Card>
                );
              })()}

              {/* Bottom Action Buttons */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, mt: 3, mb: 4 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={back}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    borderColor: "#cbd5e1",
                    color: "#64748b",
                    py: "5px",
                    px: 2.2,
                    "&:hover": { bgcolor: "#f8fafc", borderColor: "#94a3b8" },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  size="small"
                  startIcon={isLoadingSubmit ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 15 }} />}
                  disabled={isLoadingSubmit || !canInteract}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    bgcolor: BRAND_BLUE,
                    color: "#ffffff",
                    py: "5px",
                    px: 2.5,
                    "&:hover": { bgcolor: "#08223a" },
                  }}
                >
                  {isLoadingSubmit ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Grid>

            {/* Sidebar PlanSubscriptionCard (if Client Portal Signup) */}
            {isEditing && contacts.customerType === "Client Portal Signup" && (
              <Grid item xs={12} lg={4}>
                <PlanSubscriptionCard
                  contacts={contacts}
                  saasPlans={saasPlans}
                  planModules={planModules}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </Box>
    </MuiPickersUtilsProvider>
  );
};

CreateContacts.propTypes = {
  createOrUpdate: PropTypes.func.isRequired,
  messageShow: PropTypes.func.isRequired,
  upload: PropTypes.func.isRequired,
  getContact: PropTypes.func.isRequired,
  auth: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    auth: state.auth,
  };
}

export default connect(mapStateToProps, {
  createOrUpdate,
  messageShow,
  upload,
  getContact,
})(CreateContacts);